import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

export class Form {
	// --- Class Properties to cache DOM elements ---
	private forms: NodeListOf<HTMLFormElement>;
	private selectContainers: NodeListOf<HTMLElement>;
	private searchContainer: HTMLElement | null;
	private datePickerInput: HTMLInputElement | null;
	private datePickerInstance: FlatpickrInstance | null = null;
	private dateOutput: HTMLElement | null;
	private fileupload: HTMLElement | null;

	constructor() {
		// Cache all DOM elements on instantiation
		this.forms = document.querySelectorAll<HTMLFormElement>('form.delo-form');
		this.selectContainers = document.querySelectorAll<HTMLElement>('.delo-select-container');
		this.searchContainer = document.querySelector<HTMLDivElement>('.delo-search-container');
		this.datePickerInput = document.querySelector<HTMLInputElement>('#dateRange');
		this.dateOutput = document.querySelector<HTMLElement>('.date-output');
		this.fileupload = document.querySelector<HTMLElement>('.delo-file-upload');
	}

	/**
	 * Initializes all form component listeners and sets their initial state.
	 */
	public init(): void {
		this._setupSubmitLock();
		this.selectContainers.forEach((container) => this._setupSelect(container));
		if (this.searchContainer != null) this._setupSearch(this.searchContainer);
		this._setupDatepicker();

		this._setupFileUpload();

		this._setupResetAllButton();

		// Set initial state for elements that might have pre-filled values (e.g., from a previous search)
		this._initializeState();
	}

	/**
	 * Prevents duplicate form submissions and switches the submit button into a loading state.
	 */
	private _setupSubmitLock(): void {
		this.forms.forEach((form) => {
			form.addEventListener('submit', (event) => {
				const submitEvent = event as SubmitEvent;
				const submitter = submitEvent.submitter as HTMLElement | null;
				const isFinalSubmit = submitter?.closest('.submit') !== null;

				// Keep multi-step navigation untouched and only lock the final submit.
				if (!isFinalSubmit) return;

				if (form.dataset.isSubmitting === 'true') {
					event.preventDefault();
					return;
				}

				form.dataset.isSubmitting = 'true';
				form.classList.add('is-submitting');

				if (submitter instanceof HTMLButtonElement) {
					const submitContainer = submitter.closest<HTMLElement>('.submit');
					const submittingLabel =
						submitContainer?.dataset.submittingLabel?.trim() || submitter.textContent?.trim() || '';
					submitter.classList.add('is-loading');
					submitter.setAttribute('aria-busy', 'true');
					submitter.setAttribute('aria-label', submittingLabel);

					if (!submitter.dataset.originalLabel) {
						submitter.dataset.originalLabel = submitter.textContent?.trim() ?? '';
					}
					submitter.textContent = submittingLabel;

					if (!submitter.querySelector('.delo-button-spinner')) {
						const spinner = document.createElement('span');
						spinner.className = 'delo-button-spinner';
						spinner.setAttribute('aria-hidden', 'true');
						submitter.prepend(spinner);
					}
				}
			});
		});
	}

	/**
	 * Resets all inputs in the form to their default state.
	 */
	private _setupResetAllButton(): void {
		const buttonContainer = this.searchContainer?.nextElementSibling;
		const resetButton = buttonContainer?.querySelector<HTMLAnchorElement>('.reset-button');

		resetButton?.addEventListener('click', (e) => {
			e.preventDefault();

			// Reset multi-select fields
			this.selectContainers.forEach((container) => {
				const select = container.querySelector<HTMLSelectElement>('.delo-select select');
				if (select) {
					// Deselect all options
					Array.from(select.options).forEach((option) => (option.selected = false));
					// Trigger the change handler to update the UI
					this._onSelectChange(select);
				}
			});

			// Reset the single search field
			if (this.searchContainer) {
				const searchInput = this.searchContainer.querySelector<HTMLInputElement>('.delo-search');
				if (searchInput) {
					searchInput.value = '';
					searchInput.dispatchEvent(new Event('change', { bubbles: true }));
				}
			}

			// Reset the date picker
			this.datePickerInstance?.clear();
			this.setDateText(false);

			// Lastly, if the reset button also has the `reload` class, we reload the page after the filters have been cleared.
			if (resetButton.classList.contains('reload')) {
				buttonContainer?.querySelector<HTMLAnchorElement>('.delo-submit')?.click();
			}
		});
	}

	// --- Private Setup Methods ---

	/**
	 * Sets up listeners and behavior for a single select component.
	 */
	private _setupSelect(container: HTMLElement): void {
		const trigger = container.querySelector<HTMLElement>('.delo-select-trigger');
		const select = container.querySelector<HTMLSelectElement>('.delo-select select');
		const deleteSelection = container.querySelector<HTMLElement>('.delo-select-trigger .delete-selection');

		if (!select || !trigger) return;

		this._handleMobileSelectLayout(select, container);

		trigger.addEventListener('click', (e) => {
			// Use e.target instead of the deprecated e.srcElement
			if ((e.target as HTMLElement).closest('.delete-selection')) return;

			const isOpen = container.dataset.isOpen === 'true';
			container.dataset.isOpen = isOpen ? 'false' : 'true';

			if (!isOpen) {
				select.style.height = select.scrollHeight + 2 + 'px'; // +2 to avoid scrollbar

				// Logic to close when clicking outside
				document.addEventListener('click', (event) => this._closeSelectOnClickOutside(event, container));
			}
		});

		select.addEventListener('change', () => this._onSelectChange(select));

		deleteSelection?.addEventListener('click', () => {
			select.value = '';
			this._onSelectChange(select);
		});
	}

	/**
	 * Sets up listeners for a single search input component.
	 */
	private _setupSearch(container: HTMLElement): void {
		const searchInput = container.querySelector<HTMLInputElement>('.delo-search');
		const clearButton = container.querySelector<HTMLElement>('.delo-clear-search');

		if (!searchInput || !clearButton) return;

		clearButton.addEventListener('click', () => {
			searchInput.value = '';
			// Manually dispatch a 'change' event to ensure any other dependent logic runs
			searchInput.dispatchEvent(new Event('change', { bubbles: true }));
		});
	}

	/**
	 * Initializes the flatpickr date range picker.
	 */
	private _setupDatepicker(): void {
		const input = this.datePickerInput;
		if (!input) return;

		// flatpickr is code-split: only pages with a date range input fetch it
		import('flatpickr')
			.then(({ default: flatpickr }) => {
				this.datePickerInstance = flatpickr(input, {
					mode: 'range',
					dateFormat: 'Y-m-d',
					locale: { firstDayOfWeek: 1 },
					onChange: (dates) => this.setDateText(dates),
				}) as FlatpickrInstance;
			})
			.catch((error: unknown) => console.error('Date picker could not be loaded', error));

		this.dateOutput?.querySelector('.delete-selection')?.addEventListener('click', () => {
			this.datePickerInstance?.clear();
			this.setDateText(false);
		});
	}
	/**
	 * Initializes the fileupload input field.
	 */
	private _setupFileUpload(): void {
		if (this.fileupload === null) return;

		const input = this.fileupload.querySelector<HTMLInputElement>('input[type="file"].real-upload');
		const filename = this.fileupload.querySelector<HTMLElement>('.filename-preview .filename');
		const noFile = this.fileupload.querySelector<HTMLElement>('.filename-preview .no-file');
		const removeFile = this.fileupload.querySelector<HTMLElement>('.remove-file');
		if (input === null || filename === null || noFile === null || removeFile === null) return;

		input.addEventListener('change', () => {
			const file = input.files && input.files[0] ? input.files[0] : null;

			if (file) {
				filename.textContent = file.name;
				filename.classList.add('active');
				noFile.classList.remove('active');
			} else {
				noFile.classList.add('active');
				filename.classList.remove('active');
			}
		});

		removeFile.addEventListener('click', () => {
			input.value = '';
			noFile.classList.add('active');
			filename.classList.remove('active');
		});
	}

	/**
	 * Sets the initial state of the form fields on page load.
	 */
	private _initializeState(): void {
		this.selectContainers.forEach((container) => {
			const select = container.querySelector<HTMLSelectElement>('.delo-select select');

			if (select == null) return;

			this._onSelectChange(select);
		});
		this.setDateText();
	}

	// --- Private Handler & UI Update Methods ---

	/**
	 * Handles all UI updates needed when a select's value changes.
	 */
	private _onSelectChange(select: HTMLSelectElement): void {
		const container = select.closest('.delo-select-container') as HTMLElement;
		if (!container) return;

		this._updateSelectCounter(container, select.selectedOptions.length);

		this._displaySelected(select, container.nextElementSibling as HTMLElement);
	}

	/**
	 * Closes the select dropdown if a click occurs outside of it.
	 */
	private _closeSelectOnClickOutside(event: MouseEvent, container: HTMLElement): void {
		if (!container.contains(event.target as Node)) {
			container.dataset.isOpen = 'false';
		}
	}

	/**
	 * Updates the selection counter display.
	 */
	private _updateSelectCounter(container: HTMLElement, count: number): void {
		container.dataset.selectionCount = count.toString();
		const countElement = container.querySelector<HTMLElement>('.selection-count');
		if (countElement) {
			countElement.textContent = count.toString();
		}
	}

	/**
	 * Renders the selected category items visually.
	 */
	private _displaySelected(select: HTMLSelectElement, output: HTMLElement): void {
		output.innerHTML = ''; // Clear previous selections

		const selectedOptions = Array.from(select.options).filter((option) => option.selected);

		selectedOptions.forEach((option) => {
			const selectionEl = document.createElement('div');
			selectionEl.className = 'selection';
			selectionEl.innerHTML = `
                <span>${option.innerText}</span>
                <span class="delete-selection single"></span>
            `;

			selectionEl.querySelector('.delete-selection')?.addEventListener('click', () => {
				option.selected = false;
				this._onSelectChange(select);
			});

			output.appendChild(selectionEl);
		});
	}

	/**
	 * Updates the displayed text for the selected date range.
	 */
	public setDateText(dates: Date[] | false = []): void {
		if (!this.dateOutput) return;

		if (dates === false) {
			this.dateOutput.classList.add('hidden');
			return;
		}

		// If no dates are passed, try to read from hidden inputs as a fallback
		if (dates.length === 0) {
			const fromVal = document.querySelector<HTMLInputElement>('#customMinimumDate')?.value;
			const toVal = document.querySelector<HTMLInputElement>('#customMaximumDate')?.value;
			if (fromVal) dates[0] = new Date(fromVal);
			if (toVal) dates[1] = new Date(toVal);
		}

		const [from, to] = dates;
		let text = '';

		if (from && to) {
			text = `${from.toLocaleDateString()} - ${to.toLocaleDateString()}`;
		} else if (from) {
			text = from.toLocaleDateString();
		}

		const textElement = this.dateOutput.querySelector('span:not(.delete-selection)');
		if (textElement) {
			textElement.textContent = text;
		}

		// Toggle visibility based on whether a date is selected
		this.dateOutput.classList.toggle('hidden', !text);
	}

	/**
	 * Adjusts select element styles for mobile (coarse pointer) devices.
	 */
	private _handleMobileSelectLayout(select: HTMLSelectElement, container: HTMLElement): void {
		const isMobile = window.matchMedia('(pointer: coarse) and (hover: none)').matches;
		if (isMobile) {
			container.dataset.isOpen = 'true';
			// Set a fixed height on mobile to show the native select UI trigger area
			select.style.height = '34px';
		}
	}
}
